import React, { useEffect, useRef, useState } from "react";
import CustomAlert from "../../component/AlertComponent/Alert";

const getEnvValue = (key, fallback = "") => {
  return window?.__ENV__?.[key] || process.env?.[key] || fallback;
};

const SSE_SOURCES = [
  {
    key: "REACT_ALERT_API",
    label: "3PT_Message",
    url: getEnvValue("REACT_ALERT_API"),
  },
  {
    key: "REACT_NOTIFICATION_API",
    label: "Transaction",
    url: getEnvValue("REACT_NOTIFICATION_API"),
  },
  {
    key: "REACT_INFORMATION_API",
    label: "System",
    url: getEnvValue("REACT_INFORMATION_API"),
  },
];

const EVENT_COLORS = {
  System: "#1565c0",
  Transaction: "#2e7d32",
  "3PT_Message": "#6a1b9a",
  "3PT_Event": "#e65100",
};

const get3PTPayload = parsed => {
  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  return (
    parsed?.data?.msg ||
    parsed?.data?.data ||
    parsed?.msg ||
    parsed?.data ||
    parsed
  );
};

const show3PTToast = payload => {
  const status = payload?.metadata?.status;
  const message = payload?.metadata?.msg;

  console.log("[SSE-ALERT] Toast status/message:", status, message);

  if (!message) {
    console.warn("[SSE-ALERT] Missing toast message", payload);
    return;
  }

  const type = String(status || "").toUpperCase();

  if (type === "INFORMATION") {
    CustomAlert("INFO", message);
    return;
  }

  CustomAlert(type, message);
};

export default function SseMonitor() {
  const [status, setStatus] = useState("Connecting...");
  const [log, setLog] = useState([]);
  const esRefs = useRef([]);

  useEffect(() => {
    const activeSources = SSE_SOURCES.filter(source => source.url);

    if (activeSources.length === 0) {
      setStatus("No SSE URLs configured.");
      return;
    }

    const connections = activeSources.map(source => {
      const es = new EventSource(source.url);

      es.onopen = () => {
        setStatus("Connected to configured SSE streams");
      };

      es.onerror = error => {
        console.error("[SSE] Connection error:", source.label, source.url, error);
        setStatus("Connection error — check configured SSE URLs");
      };

      es.addEventListener(source.label, e => {
        let parsed;

        try {
          parsed = JSON.parse(e.data);
        } catch {
          parsed = e.data;
        }

        if (source.label === "3PT_Message") {
          console.log("[SSE-ALERT] Raw event:", source.label, parsed);

          const msgPayload = get3PTPayload(parsed);

          console.log("[SSE-ALERT] Toast payload:", msgPayload);

          show3PTToast(msgPayload);
        }

        setLog(prev => [
          {
            id: e.lastEventId || parsed?.id || "",
            event: source.label,
            json: parsed,
            time: new Date().toLocaleTimeString(),
            url: source.url,
          },
          ...prev.slice(0, 199),
        ]);
      });

      return es;
    });

    esRefs.current = connections;

    return () => {
      connections.forEach(es => es.close());
      esRefs.current = [];
    };
  }, []);

  const isConnected = status.startsWith("Connected");
  const activeSources = SSE_SOURCES.filter(source => source.url);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>SSE Event Monitor</h2>

        <p style={styles.subtitle}>
          Live events from configured env-config.js SSE endpoints.
        </p>

        <div
          style={{
            ...styles.statusBar,
            background: isConnected ? "#e8f5e9" : "#fff3e0",
          }}
        >
          <span
            style={{
              ...styles.dot,
              background: isConnected ? "#43a047" : "#fb8c00",
            }}
          />
          <span
            style={{
              color: isConnected ? "#2e7d32" : "#e65100",
              fontSize: 13,
            }}
          >
            {status}
          </span>
        </div>

        <div style={styles.legend}>
          {activeSources.map(source => (
            <span
              key={source.key}
              style={{
                ...styles.badge,
                background: EVENT_COLORS[source.label] || "#607d8b",
              }}
              title={source.url}
            >
              {source.label}
            </span>
          ))}
        </div>

        <label style={styles.label}>
          Event Log{" "}
          {log.length > 0 && <span style={styles.count}>({log.length})</span>}
        </label>

        <div style={styles.logBox}>
          {log.length === 0 ? (
            <p style={{ color: "#999", fontSize: 13, margin: 0 }}>
              Waiting for events…
            </p>
          ) : (
            log.map((entry, i) => (
              <div key={i} style={styles.logEntry}>
                <div style={styles.logHeader}>
                  <span style={styles.logTime}>{entry.time}</span>
                  <span
                    style={{
                      ...styles.badge,
                      background: EVENT_COLORS[entry.event] ?? "#607d8b",
                    }}
                  >
                    {entry.event}
                  </span>
                  <span style={styles.logId}>id: {entry.id}</span>
                </div>

                <pre style={styles.jsonBlock}>
                  {JSON.stringify(entry.json, null, 2)}
                </pre>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "40px 20px",
    minHeight: "100%",
    background: "#f0f2f5",
  },
  card: {
    background: "white",
    borderRadius: 10,
    padding: 40,
    width: 680,
    boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
  },
  title: {
    color: "#1a237e",
    fontSize: 20,
    marginBottom: 6,
  },
  subtitle: {
    color: "#666",
    fontSize: 13,
    marginBottom: 20,
  },
  statusBar: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 6,
    marginBottom: 16,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 8,
    flexShrink: 0,
  },
  legend: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    marginBottom: 6,
  },
  count: {
    fontWeight: 400,
    color: "#888",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    color: "white",
    flexShrink: 0,
  },
  logBox: {
    maxHeight: 500,
    overflowY: "auto",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: "8px 12px",
    background: "#fafafa",
  },
  logEntry: {
    marginBottom: 12,
    borderBottom: "1px solid #eeeeee",
    paddingBottom: 12,
  },
  logHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 6,
  },
  logTime: {
    color: "#999",
    fontSize: 11,
    flexShrink: 0,
  },
  logId: {
    color: "#bbb",
    fontSize: 11,
    marginLeft: "auto",
  },
  jsonBlock: {
    margin: 0,
    padding: "10px 14px",
    background: "#1e1e1e",
    color: "#d4d4d4",
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.6,
    overflowX: "auto",
    whiteSpace: "pre",
    fontFamily: "monospace",
  },
};